import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  type ClubKind,
  type ClubRow,
  type ClubsResponse,
  type CreateClubRequest,
  type CreateClubResponse,
  type DeleteClubResponse,
  type UpdateClubRequest,
  type UpdateClubResponse,
} from "../api";
import { useClubs } from "../ClubContext";
import { ClubLogoUpload } from "../components/ClubLogoUpload";

const KIND_LABELS: Record<ClubKind, string> = {
  club: "Club",
  highSchool: "High school",
  college: "College",
};

// The zones a US water polo club plausibly sits in. Free text would invite
// typos into a NOT NULL column; a full IANA list is 400 entries of noise. A
// club whose stored zone isn't here keeps it — see timezoneOptions.
const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "Pacific/Honolulu",
  "America/Anchorage",
];

function timezoneOptions(current: string): string[] {
  return TIMEZONES.includes(current) ? TIMEZONES : [current, ...TIMEZONES];
}

/**
 * Mirrors the server's slugify so the form can preview what a blank slug
 * becomes. The server remains the authority — this is a hint, not validation.
 * Apostrophes are dropped rather than hyphenated ("Ben's Club" -> "bens-club");
 * separators like "&" stay hyphens, since those sit between words.
 */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The fields create and edit have in common. */
interface ClubFormValues {
  name: string;
  kind: ClubKind;
  slug: string;
  shortName: string;
  timezone: string;
  city: string;
  state: string;
  website: string;
}

const EMPTY_VALUES: ClubFormValues = {
  name: "",
  kind: "club",
  slug: "",
  shortName: "",
  timezone: "America/Los_Angeles",
  city: "",
  state: "",
  website: "",
};

function toFormValues(club: ClubRow): ClubFormValues {
  return {
    name: club.name,
    kind: club.kind,
    slug: club.slug ?? "",
    shortName: club.shortName ?? "",
    timezone: club.timezone,
    city: club.city ?? "",
    state: club.state ?? "",
    website: club.website ?? "",
  };
}

/**
 * Only the fields that actually changed. Sending the whole form would work,
 * but a PATCH that names untouched columns is a lie about intent — and it
 * would rewrite a slug on every save, which is the one column worth leaving
 * alone unless it's deliberately being changed.
 */
function changedFields(before: ClubFormValues, after: ClubFormValues): UpdateClubRequest {
  const patch: Record<string, string> = {};
  (Object.keys(after) as (keyof ClubFormValues)[]).forEach((key) => {
    if (after[key] !== before[key]) patch[key] = after[key];
  });
  return patch as UpdateClubRequest;
}

const inputClass = "mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm";
const labelClass = "block text-xs font-semibold text-slate-600";

function ClubFields({
  values,
  onChange,
}: {
  values: ClubFormValues;
  onChange: <K extends keyof ClubFormValues>(key: K, value: ClubFormValues[K]) => void;
}) {
  const slugPreview = values.slug.trim() || slugify(values.name);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label className={labelClass}>Club name</label>
        <input
          type="text"
          required
          value={values.name}
          onChange={(e) => onChange("name", e.target.value)}
          className={inputClass}
          placeholder="680 Water Polo"
        />
      </div>

      <div>
        <label className={labelClass}>Type</label>
        <select
          value={values.kind}
          onChange={(e) => onChange("kind", e.target.value as ClubKind)}
          className={inputClass}
        >
          {(Object.keys(KIND_LABELS) as ClubKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass}>
          Short name <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="text"
          value={values.shortName}
          onChange={(e) => onChange("shortName", e.target.value)}
          className={inputClass}
          placeholder="680"
        />
      </div>

      <div className="sm:col-span-2">
        <label className={labelClass}>URL slug</label>
        <input
          type="text"
          value={values.slug}
          onChange={(e) => onChange("slug", e.target.value)}
          className={`${inputClass} font-mono`}
          placeholder={slugify(values.name) || "680-water-polo"}
        />
        <p className="mt-1 text-xs text-slate-500">
          {slugPreview ? (
            <>
              Public address will be <span className="font-mono">/{slugPreview}</span>.
            </>
          ) : (
            "Derived from the club name if you leave this blank."
          )}{" "}
          Changing it breaks any link people have already saved.
        </p>
      </div>

      <div>
        <label className={labelClass}>Timezone</label>
        <select
          value={values.timezone}
          onChange={(e) => onChange("timezone", e.target.value)}
          className={inputClass}
        >
          {timezoneOptions(values.timezone).map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className={labelClass}>
            City <span className="font-normal text-slate-400">optional</span>
          </label>
          <input
            type="text"
            value={values.city}
            onChange={(e) => onChange("city", e.target.value)}
            className={inputClass}
            placeholder="Danville"
          />
        </div>
        <div>
          <label className={labelClass}>State</label>
          <input
            type="text"
            value={values.state}
            onChange={(e) => onChange("state", e.target.value)}
            className={inputClass}
            placeholder="CA"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label className={labelClass}>
          Website <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="url"
          value={values.website}
          onChange={(e) => onChange("website", e.target.value)}
          className={inputClass}
          placeholder="https://example.com"
        />
      </div>
    </div>
  );
}

export function ClubsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, loading: clubsLoading, refresh } = useClubs();

  const [data, setData] = useState<ClubsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createValues, setCreateValues] = useState<ClubFormValues>(EMPTY_VALUES);
  const [adminEmail, setAdminEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const [editing, setEditing] = useState<ClubRow | null>(null);
  const [editValues, setEditValues] = useState<ClubFormValues>(EMPTY_VALUES);
  const [saving, setSaving] = useState(false);
  const [editMessage, setEditMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const loadClubs = useCallback(async (): Promise<ClubRow[] | null> => {
    try {
      const res = await api<ClubsResponse>("/v1/clubs");
      setData(res);
      setError(null);
      return res.clubs;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load";
      setError(msg);
      if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
      return null;
    }
  }, [navigate]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadClubs();
  }, [isSuperAdmin, loadClubs]);

  function startEditing(club: ClubRow) {
    setEditing(club);
    setEditValues(toFormValues(club));
    setEditMessage(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateMessage(null);

    // Send only what was filled in — the server applies its own defaults, and
    // an empty string is not the same as "unset" for a nullable column.
    const body: CreateClubRequest = {
      name: createValues.name.trim(),
      kind: createValues.kind,
      adminEmail: adminEmail.trim(),
      timezone: createValues.timezone,
    };
    if (createValues.slug.trim()) body.slug = createValues.slug.trim();
    if (createValues.shortName.trim()) body.shortName = createValues.shortName.trim();
    if (createValues.city.trim()) body.city = createValues.city.trim();
    if (createValues.state.trim()) body.state = createValues.state.trim();
    if (createValues.website.trim()) body.website = createValues.website.trim();

    try {
      const res = await api<CreateClubResponse>("/v1/clubs", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setCreateMessage({
        ok: true,
        text: `Created ${res.club.name} (/${res.club.slug}). Invited ${res.invitation.email} as Club Admin.`,
      });
      setCreateValues(EMPTY_VALUES);
      setAdminEmail("");
      await loadClubs();
      // A super admin reaches every club, so the new one belongs in the
      // switcher immediately rather than after a reload.
      await refresh();
    } catch (err) {
      setCreateMessage({ ok: false, text: err instanceof Error ? err.message : "Create failed" });
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;

    const patch = changedFields(toFormValues(editing), editValues);
    if (Object.keys(patch).length === 0) {
      // The API answers an empty patch with 400. Nothing changed is not an
      // error from where the user is standing, so just close.
      setEditing(null);
      return;
    }

    setSaving(true);
    setEditMessage(null);
    try {
      const res = await api<UpdateClubResponse>(`/v1/clubs/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      setEditing(null);
      setCreateMessage(null);
      await loadClubs();
      // The club's name may be showing in the switcher right now.
      await refresh();
      setEditMessage({ ok: true, text: `Saved ${res.club.name}.` });
    } catch (err) {
      setEditMessage({ ok: false, text: err instanceof Error ? err.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(club: ClubRow) {
    // Every club has a slug — migration 0018 backfilled the old ones and the
    // API assigns one on create — but the column is nullable, and confirming a
    // deletion against "null" is not a thing to leave to chance.
    if (!club.slug) {
      alert(`${club.name} has no slug, which deletion uses as confirmation. Give it one first.`);
      return;
    }

    // The API requires the slug as confirmation; asking for the same thing here
    // keeps the destructive step deliberate rather than one stray click.
    const typed = window.prompt(
      `Delete ${club.name}?\n\nThis removes its teams, players, age groups, and memberships. ` +
        `Games are never deleted this way — a club with any recorded game is refused.\n\n` +
        `Type the slug "${club.slug}" to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== club.slug) {
      alert(`That didn't match "${club.slug}" — nothing was deleted.`);
      return;
    }

    try {
      const res = await api<DeleteClubResponse>(
        `/v1/clubs/${club.id}?confirm=${encodeURIComponent(club.slug)}`,
        { method: "DELETE" }
      );
      if (editing?.id === club.id) setEditing(null);
      setEditMessage({ ok: true, text: `Deleted ${res.name}.` });
      await loadClubs();
      // The deleted club may have been the active one; refresh lets the
      // provider fall back to a club that still exists.
      await refresh();
    } catch (err) {
      setEditMessage({ ok: false, text: err instanceof Error ? err.message : "Delete failed" });
    }
  }

  if (!isSuperAdmin && !clubsLoading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        <p className="text-sm text-slate-500">
          This page is for platform super admins. Ask an admin of your club for access to the
          pages you need.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
      <section>
        <h2 className="mb-2 text-lg font-semibold">Clubs</h2>
        <p className="mb-4 text-sm text-slate-500">
          Every club on the platform. You can reach all of them from the club switcher, whether
          or not you're a member.
        </p>
        {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
        {editMessage && (
          <p className={`mb-4 text-sm ${editMessage.ok ? "text-emerald-600" : "text-rose-600"}`}>
            {editMessage.text}
          </p>
        )}
        {data === null && !error && <p className="text-sm text-slate-500">Loading…</p>}
        {data && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">Club</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Slug</th>
                  <th className="px-4 py-2">Location</th>
                  <th className="px-4 py-2 text-right">Members</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.clubs.map((c) => (
                  <tr
                    key={c.id}
                    className={`border-t border-slate-100 ${
                      editing?.id === c.id ? "bg-brand-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {c.logoUrl && (
                          <img
                            src={c.logoUrl}
                            alt=""
                            className="h-6 w-6 shrink-0 rounded object-contain"
                          />
                        )}
                        <span className="font-medium">{c.name}</span>
                        {c.shortName && (
                          <span className="text-xs text-slate-500">{c.shortName}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{KIND_LABELS[c.kind]}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.slug ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {c.memberCount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        onClick={() => startEditing(c)}
                        className="text-xs font-medium text-brand-700 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        className="ml-3 text-xs font-medium text-rose-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <section>
          <h2 className="mb-2 text-lg font-semibold">Edit {editing.name}</h2>
          <p className="mb-4 text-sm text-slate-500">
            Slug and type are super-admin only — which is why they're editable here.
          </p>
          <form onSubmit={handleSave} className="rounded-xl border border-brand-200 bg-white p-4">
            <ClubFields
              values={editValues}
              onChange={(key, value) => setEditValues((v) => ({ ...v, [key]: value }))}
            />
            <div className="mt-4">
              <ClubLogoUpload
                clubId={editing.id}
                clubName={editing.name}
                logoUrl={editing.logoUrl}
                onChanged={async () => {
                  const clubs = await loadClubs();
                  // `editing` is a snapshot taken when the form opened, so it
                  // still holds the old logo URL — re-point it at the fresh row
                  // or the preview reverts to the previous image.
                  const fresh = clubs?.find((c) => c.id === editing.id);
                  if (fresh) setEditing(fresh);
                  // The switcher shows the logo too.
                  await refresh();
                }}
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-700"
              >
                Cancel
              </button>
              {editMessage && !editMessage.ok && (
                <p className="text-sm text-rose-600">{editMessage.text}</p>
              )}
            </div>
          </form>
        </section>
      )}

      {!editing && (
        <section>
          <h2 className="mb-2 text-lg font-semibold">New club</h2>
          <p className="mb-4 text-sm text-slate-500">
            The first Club Admin is invited by email as part of creating the club — they set it
            up from there.
          </p>
          <form onSubmit={handleCreate} className="rounded-xl border border-slate-200 bg-white p-4">
            <ClubFields
              values={createValues}
              onChange={(key, value) => setCreateValues((v) => ({ ...v, [key]: value }))}
            />
            <div className="mt-4">
              <label className={labelClass}>First Club Admin email</label>
              <input
                type="email"
                required
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className={`${inputClass} sm:max-w-md`}
                placeholder="coach@example.com"
              />
              <p className="mt-1 text-xs text-slate-500">
                They get an invitation email. A club with no admin can't be run by anyone but
                you.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={creating}
                className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {creating ? "Creating…" : "Create club"}
              </button>
              {createMessage && (
                <p className={`text-sm ${createMessage.ok ? "text-emerald-600" : "text-rose-600"}`}>
                  {createMessage.text}
                </p>
              )}
            </div>
          </form>
        </section>
      )}
    </main>
  );
}
